// Function index:
//   mountRoomScene(parent, opts) — build a DOM/SVG room-graph scene for
//       tower / labyrinth / dungeon worlds. Distinct renderer from the
//       Pixi hex scene; rooms are positioned absolutely at (x, y) layout
//       coords, doors are SVG <line>s between centres. Click a connected
//       room to attempt a move; the server validates door connectivity.
//
// Re-exports the public types so callers don't need to know the internal
// layout. Static pieces live in siblings:
//   room-scene/types.ts     — input/output interfaces
//   room-scene/layout.ts    — pixel constants + escapeHtml + extents
//   room-scene/dom.ts       — scene + door line + room card construction
//   room-scene/refresh.ts   — fog-of-war repaint (current / visited /
//                              reachable + door visibility + scroll-into-view)
//
// Why DOM + SVG instead of Pixi: room graphs are small (~10-20 nodes per
// dungeon) and need readable room names + descriptions on hover. Native
// DOM gives us free text, accessibility, and zero new render code.

import type {
  BootstrapRoom, RoomCharacterInput, RoomNpcInput,
  RoomSceneMount, RoomSceneOptions,
} from './room-scene/types.js';
import { sceneExtents } from './room-scene/layout.js';
import { buildDoorLines, buildRoomCards, buildScene } from './room-scene/dom.js';
import { refreshScene, type RefreshCtx } from './room-scene/refresh.js';

export type { RoomCharacterInput, RoomNpcInput, RoomSceneMount, RoomSceneOptions };

export function mountRoomScene(parent: HTMLElement, opts: RoomSceneOptions): RoomSceneMount {
  // Indexes: client-side door connectivity gives instant click feedback
  // (server still validates), and visitedIds drives the live fog refresh.
  const roomByKey = new Map<string, BootstrapRoom>(
    opts.rooms.map((r) => [`${r.x},${r.y}`, r]),
  );
  const roomById = new Map<number, BootstrapRoom>(opts.rooms.map((r) => [r.id, r]));
  const connectedFrom = new Map<number, Set<number>>();
  for (const d of opts.doors) {
    if (d.locked) continue;
    const set = connectedFrom.get(d.from_room_id) ?? new Set<number>();
    set.add(d.to_room_id);
    connectedFrom.set(d.from_room_id, set);
  }
  // NM-G4: track visited locally so the live setSelfPosition can mark
  // the current room visited and refresh fog without a server round-trip.
  const visitedIds = new Set<number>(opts.rooms.filter((r) => r.visited).map((r) => r.id));

  // NPCs live in a Map<id, RoomNpcInput> so moveNpc can shuffle them
  // between rooms between npc-sim ticks. npcsByKey is derived for the
  // O(1) "who's in this room" lookup refresh() needs.
  const npcs = new Map<number, RoomNpcInput>();
  for (const n of (opts.npcs ?? [])) npcs.set(n.id, { ...n });
  const npcsByKey = new Map<string, RoomNpcInput[]>();
  const rebuildNpcIndex = (): void => {
    npcsByKey.clear();
    for (const n of npcs.values()) {
      const k = `${n.q},${n.r}`;
      const arr = npcsByKey.get(k) ?? [];
      arr.push(n);
      npcsByKey.set(k, arr);
    }
  };
  rebuildNpcIndex();

  // Multi-character display: a live map id → character so other party
  // members appear in their room, follow you across rooms, and disappear
  // when they leave the world.
  const characters = new Map<number, RoomCharacterInput>();
  for (const c of (opts.characters ?? [])) characters.set(c.id, { ...c });

  const ext = sceneExtents(opts.rooms);
  const { root, inner, svg } = buildScene(parent, ext);
  const doorLines = buildDoorLines(svg, opts.doors, roomById);

  let clickHandler: ((q: number, r: number) => void) | null = null;
  const roomEl = buildRoomCards({
    inner,
    rooms: opts.rooms,
    onClick: (room) => clickHandler?.(room.x, room.y),
  });

  const ctx: RefreshCtx = {
    root, roomEl, roomById, roomByKey, connectedFrom,
    visitedIds, doorLines, characters, npcsByKey,
  };
  refreshScene(ctx, opts.selfQ, opts.selfR);

  // Track the current self position so other-character moves can
  // re-render without recomputing the highlight.
  let selfQ = opts.selfQ;
  let selfR = opts.selfR;

  return {
    setSelfPosition(q, r) {
      selfQ = q; selfR = r;
      // Keep the self entry in the characters map in sync so chips
      // follow the player around the dungeon.
      for (const c of characters.values()) {
        if (c.isSelf) { c.q = q; c.r = r; }
      }
      refreshScene(ctx, q, r);
    },
    moveCharacter(id, q, r) {
      const c = characters.get(id);
      if (!c) return;
      c.q = q; c.r = r;
      if (c.isSelf) { selfQ = q; selfR = r; }
      refreshScene(ctx, selfQ, selfR);
    },
    addCharacter(c) {
      characters.set(c.id, { ...c });
      refreshScene(ctx, selfQ, selfR);
    },
    removeCharacter(id) {
      characters.delete(id);
      refreshScene(ctx, selfQ, selfR);
    },
    moveNpc(id, q, r) {
      const n = npcs.get(id);
      if (!n) return;
      n.q = q; n.r = r;
      rebuildNpcIndex();
      refreshScene(ctx, selfQ, selfR);
    },
    onRoomClick(handler) { clickHandler = handler; },
    destroy() {
      parent.removeChild(root);
    },
  };
}
